import { ManagedPaymentMethod, getPaymentMethods } from './Managed'

// ---------------------------------------------------------------------------------------------------------------------
// How assets and networks are named on screen. The registry ids ("OpBnb", "SepoliaTestnet", "BitcoinCash") are what
// the API, saved accounts and deep links use, so they stay as they are; everything a person reads comes from here.
//
// The rules, so every selector, chip and review line agree:
//   - an asset is its ticker first, with its name beside it: "USDC · USD Coin", "ETH · Ethereum"
//   - the network is always spelled out and always the same way: "Polygon", "BNB Smart Chain", "Sepolia testnet"
//   - one line: "USDC on Polygon"; two lines: "USDC" over "USD Coin · Polygon"
//   - the icon is the asset's own logo; a small badge carries the network whenever the asset is not on its home chain
// ---------------------------------------------------------------------------------------------------------------------

/** Registry network id to the name people know it by. */
export const NETWORK_NAMES: Record<string, string> = {
  Ethereum: 'Ethereum',
  Arbitrum: 'Arbitrum',
  Base: 'Base',
  Optimism: 'Optimism',
  Polygon: 'Polygon',
  BNB: 'BNB Smart Chain',
  OpBnb: 'opBNB',
  Avalanche: 'Avalanche',
  Solana: 'Solana',
  Tron: 'Tron',
  Bitcoin: 'Bitcoin',
  Litecoin: 'Litecoin',
  Dogecoin: 'Dogecoin',
  BitcoinCash: 'Bitcoin Cash',
  BitcoinSV: 'Bitcoin SV',
  Polkadot: 'Polkadot',
  PolkadotAssetHub: 'Polkadot Asset Hub',
  EVM: 'EVM networks',
  Fiat: 'Bank transfer',
  SepoliaTestnet: 'Sepolia testnet',
  PolygonTestnet: 'Polygon testnet',
  SolanaDevnet: 'Solana devnet',
  TronTestnet: 'Tron testnet',
  BNBTestnet: 'BNB Smart Chain testnet',
  BitcoinTestnet: 'Bitcoin testnet',
  LitecoinTestnet: 'Litecoin testnet',
  BitcoinCashTestnet: 'Bitcoin Cash testnet',
  PolkadotTestnet: 'Polkadot testnet',
  PolkadotAssetHubTestnet: 'Polkadot Asset Hub testnet'
}

/** The mainnet a test network stands in for. */
const MAINNET_OF: Record<string, string> = {
  SepoliaTestnet: 'Ethereum',
  PolygonTestnet: 'Polygon',
  SolanaDevnet: 'Solana',
  TronTestnet: 'Tron',
  BNBTestnet: 'BNB',
  BitcoinTestnet: 'Bitcoin',
  LitecoinTestnet: 'Litecoin',
  BitcoinCashTestnet: 'BitcoinCash',
  PolkadotTestnet: 'Polkadot',
  PolkadotAssetHubTestnet: 'PolkadotAssetHub'
}

/** Ticker to the name people know the asset by. */
export const ASSET_NAMES: Record<string, string> = {
  BTC: 'Bitcoin',
  LTC: 'Litecoin',
  DOGE: 'Dogecoin',
  BCH: 'Bitcoin Cash',
  BSV: 'Bitcoin SV',
  ETH: 'Ethereum',
  SOL: 'Solana',
  POL: 'Polygon',
  MATIC: 'Polygon',
  AVAX: 'Avalanche',
  BNB: 'BNB',
  TRX: 'Tron',
  DOT: 'Polkadot',
  PAS: 'Paseo',
  USDC: 'USD Coin',
  'USDC.E': 'USD Coin (bridged)',
  USDT: 'Tether',
  EURC: 'Euro Coin',
  BUSD: 'Binance USD',
  BAT: 'Basic Attention Token',
  USD: 'US dollar',
  EUR: 'Euro',
  GBP: 'British pound',
  BRL: 'Brazilian real',
  MXN: 'Mexican peso',
  CAD: 'Canadian dollar',
  AUD: 'Australian dollar',
  CHF: 'Swiss franc',
  JPY: 'Japanese yen',
  PLN: 'Polish złoty',
  SEK: 'Swedish krona',
  NOK: 'Norwegian krone',
  DKK: 'Danish krone',
  CZK: 'Czech koruna',
  HUF: 'Hungarian forint',
  SGD: 'Singapore dollar',
  HKD: 'Hong Kong dollar',
  NZD: 'New Zealand dollar',
  ZAR: 'South African rand',
  INR: 'Indian rupee',
  AED: 'UAE dirham',
  TRY: 'Turkish lira',
  NGN: 'Nigerian naira'
}

/** The chain a native coin belongs to; a native coin elsewhere ("ETH on Arbitrum") carries that network as a badge. */
const HOME_NETWORK: Record<string, string> = {
  BTC: 'Bitcoin',
  LTC: 'Litecoin',
  DOGE: 'Dogecoin',
  BCH: 'BitcoinCash',
  BSV: 'BitcoinSV',
  ETH: 'Ethereum',
  SOL: 'Solana',
  POL: 'Polygon',
  AVAX: 'Avalanche',
  BNB: 'BNB',
  TRX: 'Tron',
  DOT: 'Polkadot',
  PAS: 'Polkadot'
}

const TICKER_LABELS: Record<string, string> = { 'USDC.E': 'USDC.e' }

/** "Bitcoin Cash" for "BitcoinCash", "Sepolia testnet" for "SepoliaTestnet"; unknown ids split on their capitals. */
export const networkName = (id: string | null | undefined): string => {
  if (!id) return ''
  const known = NETWORK_NAMES[id]
  if (known) return known
  return id
    .replace(/Testnet$/, ' testnet')
    .replace(/Devnet$/, ' devnet')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
}

/** "Sepolia testnet" and friends read as test networks; "Fiat" is not a network at all. */
export const isTestNetwork = (id: string | null | undefined): boolean => !!id && id in MAINNET_OF
export const isNetworkId = (id: string | null | undefined): boolean => !!id && id !== 'Fiat'

/** How a ticker is written: as itself, except the few whose case matters. */
export const tickerLabel = (ticker: string): string => TICKER_LABELS[ticker.toUpperCase()] ?? ticker

/** "USD Coin" for USDC, "Ethereum" for ETH; a ticker nobody has named is shown as itself. */
export const assetName = (ticker: string | null | undefined): string => {
  if (!ticker) return ''
  return ASSET_NAMES[ticker.toUpperCase()] ?? ticker
}

/** The registry network id a method settles on. */
export const methodNetworkId = (method: ManagedPaymentMethod): string => method.hostChain?.name ?? method.name

/** The network a method settles on, as people read it; empty for a bank currency. */
export const methodNetworkLabel = (method: ManagedPaymentMethod): string =>
  method.isFiat || method.name === 'Fiat' ? '' : networkName(methodNetworkId(method))

/**
 * The asset's own name: "Ethereum" for ETH wherever it is, "Tether" for USDT, "Euro" for EUR. A wrapped
 * token keeps the name the registry gives it, so it is never mistaken for the coin it wraps.
 */
export const methodAssetName = (method: ManagedPaymentMethod): string =>
  method.hostChain && /^wrapped\b/i.test(method.name) ? method.name : assetName(method.ticker)

/** Whether the asset sits on a chain other than its own, which is when its icon carries the network. */
export const methodIsOffHomeNetwork = (method: ManagedPaymentMethod): boolean => {
  if (method.isFiat || method.name === 'Fiat') return false
  const network = methodNetworkId(method)
  const mainnet = MAINNET_OF[network] ?? network
  const home = HOME_NETWORK[method.ticker.toUpperCase()]
  return !home || home !== mainnet
}

let nativeLogos: Record<string, any> | undefined

/** The logo of the asset itself: the token's own logo, or for a native coin the logo of its home chain. */
export const methodAssetLogo = (method: ManagedPaymentMethod): any => {
  if (method.hostChain || method.isFiat) return method.logo
  const home = HOME_NETWORK[method.ticker.toUpperCase()]
  if (!home || home === method.name) return method.logo
  if (!nativeLogos) {
    nativeLogos = {}
    for (const m of getPaymentMethods()) {
      if (!m.hostChain && !m.isFiat && m.logo) nativeLogos[m.name] = m.logo
    }
  }
  return nativeLogos[home] ?? method.logo
}

/** The logo of the network the method settles on. */
export const methodNetworkLogo = (method: ManagedPaymentMethod): any => method.hostChain?.logo ?? method.logo

/** One line: "USDC on Polygon", or just "USDC" when the network is not wanted (unified balances) or there is none. */
export const assetLine = (method: ManagedPaymentMethod, options: { network?: boolean } = {}): string => {
  const ticker = tickerLabel(method.ticker)
  const network = options.network === false ? '' : methodNetworkLabel(method)
  return network ? `${ticker} on ${network}` : ticker
}

/** The second line under a ticker: "USD Coin · Polygon", "Ethereum · Arbitrum", "Tron", "Euro". */
export const assetSubtitle = (method: ManagedPaymentMethod, options: { network?: boolean } = {}): string => {
  const name = methodAssetName(method)
  const network = options.network === false ? '' : methodNetworkLabel(method)
  if (!network || network === name) return name
  // "BNB on BNB Smart Chain" is the chain's own coin: the network already says it.
  if (network.startsWith(`${name} `)) return network
  return `${name} · ${network}`
}

/** "USDC on Polygon" from the pieces a record carries, for rows that have no registry method at hand. */
export const assetLineFor = (ticker: string | null | undefined, network: string | null | undefined): string => {
  const t = ticker ? tickerLabel(ticker) : ''
  const n = isNetworkId(network) ? networkName(network) : ''
  if (t && n) return `${t} on ${n}`
  return t || n
}
